/**
 * Utility functions for determining passage dates for timeline initialization
 */

/**
 * Determine the initial year for a passage's timeline slider
 *
 * Priority order:
 * 1. Passage-specific date from AI (historicalEventsByPassage.passageDate)
 * 2. Passage-specific event (eventType === 'passage-event' in historicalEvents)
 * 3. Narrative setting for the book (historicalEventsByBook.narrativeSetting)
 * 4. null if no date can be determined
 *
 * @param passage - The passage object
 * @param historicalEvents - All historical events (for passage-event lookup)
 * @param historicalEventsByPassage - Passage-specific historical events data
 * @param historicalEventsByBook - Book-level historical events data (contains narrativeSetting)
 * @returns The start year for the passage, or null if not determinable
 */
export function calculatePassageDate(
    passage: Passage | undefined,
    historicalEvents: HistoricalEvent[],
    historicalEventsByPassage: HistoricalEventsByPassage | null,
    historicalEventsByBook: HistoricalEventsByBook | null
): number | null {
    if (!passage) return null;

    // 1. Passage-specific date from AI
    if (historicalEventsByPassage?.passageDate) {
        return historicalEventsByPassage.passageDate.startYear;
    }

    // 2. Search for passage-specific event in historicalEvents collection
    if (historicalEvents.length > 0) {
        const passageEvent = historicalEvents.find(
            event => event.eventType === 'passage-event' && event.passageId === passage.id
        );
        if (passageEvent) {
            return passageEvent.date.startYear;
        }
    }

    // 3. Use narrative setting from HistoricalEventsByBook
    if (historicalEventsByBook?.narrativeSetting?.narrativeSettingDate) {
        return historicalEventsByBook.narrativeSetting.narrativeSettingDate.startYear;
    }

    return null;
}

